import { Bell, BookOpen, CheckCheck, ClipboardList, Megaphone, TimerReset } from 'lucide-react';
import { useEffect } from 'react';
import { Link, useParams } from 'react-router';
import { Markdown } from '@/components/Markdown';
import { Badge, Button, EmptyState, PageHeader, QueryError, Spinner } from '@/components/ui';
import { cn, formatDateTime, relativeTime } from '@/lib/utils';
import type { NotificationRow } from '@/types/db';
import { useAnnouncement, useMarkRead, useNotifications } from './api';

const KIND_ICON: Record<NotificationRow['kind'], typeof Bell> = {
  announcement: Megaphone,
  assignment: ClipboardList,
  protocol_update: BookOpen,
  reminder: TimerReset,
  system: Bell,
};

export function InboxPage() {
  const { data, isPending, isError, error } = useNotifications();
  const markRead = useMarkRead();
  const unread = data?.filter((n) => !n.read_at).length ?? 0;

  return (
    <div>
      <PageHeader
        title="Inbox"
        subtitle="Announcements, protocol updates, and assignments"
        actions={
          unread > 0 && (
            <Button size="sm" variant="outline" icon={<CheckCheck className="size-4" />} loading={markRead.isPending} onClick={() => markRead.mutate('all')}>
              Mark all read
            </Button>
          )
        }
      />
      {isPending ? (
        <Spinner />
      ) : isError ? (
        <QueryError error={error} />
      ) : data.length === 0 ? (
        <EmptyState icon={<Bell className="size-10" />} title="You’re all caught up" />
      ) : (
        <ul className="divide-y divide-stone-200 border-y border-stone-200 dark:divide-stone-800 dark:border-stone-800">
            {data.map((n) => {
              const Icon = KIND_ICON[n.kind];
              const target = n.kind === 'announcement' && n.announcement_id ? `/inbox/${n.announcement_id}` : (n.url ?? '/inbox');
              return (
                <li key={n.id}>
                  <Link
                    to={target}
                    onClick={() => !n.read_at && markRead.mutate([n.id])}
                    className={cn('flex gap-3 px-1 py-3.5 transition-colors hover:bg-stone-100 dark:hover:bg-ink-900', !n.read_at && 'bg-white dark:bg-ink-900')}
                  >
                    <Icon className={cn('mt-0.5 size-4 shrink-0', n.priority === 'critical' ? 'text-red-600' : n.priority === 'important' ? 'text-amber-600' : 'text-stone-400')} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className={cn('font-medium', !n.read_at && 'font-semibold')}>{n.title}</span>
                        {n.priority !== 'info' && <Badge tone={n.priority === 'critical' ? 'danger' : 'warning'}>{n.priority}</Badge>}
                      </div>
                      {n.body && <p className="line-clamp-2 text-sm text-stone-600 dark:text-stone-400">{n.body}</p>}
                      <p className="mt-1 font-mono text-xs text-stone-500">{relativeTime(n.created_at)}</p>
                    </div>
                    {!n.read_at && <span className="mt-1.5 size-2 shrink-0 rounded-full bg-red-600" aria-label="Unread" />}
                  </Link>
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
}

export function AnnouncementPage() {
  const { id } = useParams();
  const { data, isPending, isError, error } = useAnnouncement(id);
  const notifications = useNotifications();
  const markRead = useMarkRead();

  useEffect(() => {
    const unreadIds = notifications.data?.filter((n) => n.announcement_id === id && !n.read_at).map((n) => n.id) ?? [];
    if (unreadIds.length > 0) markRead.mutate(unreadIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, notifications.data]);

  if (isPending) return <Spinner />;
  if (isError) return <QueryError error={error} />;
  return (
    <div>
      <PageHeader back={{ to: '/inbox', label: 'Inbox' }} title={data.title} subtitle={formatDateTime(data.created_at)} />
      {data.priority !== 'info' && (
        <Badge tone={data.priority === 'critical' ? 'danger' : 'warning'} className="mb-3">
          {data.priority}
        </Badge>
      )}
      <div className="max-w-3xl">
        <Markdown>{data.body_md || '_No additional details._'}</Markdown>
        {data.protocol_id && (
          <Link to={`/protocols/${data.protocol_id}`} className="mt-4 inline-flex items-center gap-2 font-semibold text-stone-950 underline-offset-[3px] hover:underline dark:text-white">
            <BookOpen className="size-4" /> View protocol
          </Link>
        )}
      </div>
    </div>
  );
}
